/*
 * Ported from the IDV proof of concept (frontend/src/utils/documentCrop.ts).
 *
 * Mechanical TypeScript -> JavaScript conversion (tsc --target ES2020, types
 * erased). The algorithms, thresholds and geometry are unchanged: this file is
 * pinned by src/utils/documentCrop.test.js and by the IDV backend contract.
 */

/**
 * Pure document-crop geometry.
 *
 * This module contains no React and no DOM access. It works in three explicit
 * coordinate spaces and never mixes them in a single calculation:
 *
 *  1. DOM coordinates       — CSS pixels from `getBoundingClientRect()` for the
 *                             `<video>` element and the overlay frame.
 *  2. preview-source pixels — pixels of the live video stream
 *                             (`video.videoWidth` × `video.videoHeight`). The
 *                             overlay was displayed over THIS stream.
 *  3. native-photo pixels   — pixels of the still returned by
 *                             `ImageCapture.takePhoto()`, which may have a
 *                             different aspect ratio / field of view.
 *
 * The overlay is projected into preview-source pixels first, normalized against
 * the full preview source, and only then mapped into the native photo through a
 * centered "active region" that represents the preview's field of view inside
 * the still. See `computeCenteredActiveRegion` for the documented assumption.
 */
export const CENTER_OBJECT_POSITION = { x: 0.5, y: 0.5 };
/** Fixed 3% safety margin added on every edge of the overlay. */
export const DOCUMENT_CROP_MARGIN_RATIO = 0.03;
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export function isPositiveSize(size) {
    return (!!size &&
        Number.isFinite(size.width) &&
        Number.isFinite(size.height) &&
        size.width > 0 &&
        size.height > 0);
}
export function isUsableRect(rect) {
    return (!!rect &&
        Number.isFinite(rect.x) &&
        Number.isFinite(rect.y) &&
        Number.isFinite(rect.width) &&
        Number.isFinite(rect.height) &&
        rect.width > 0 &&
        rect.height > 0);
}
/**
 * Clamp a rectangle so it stays inside `[0, size]` on both axes and can never
 * produce a negative width or height. Floating-point precision is preserved.
 */
export function clampRectToSource(rect, size) {
    const x = clamp(rect.x, 0, size.width);
    const y = clamp(rect.y, 0, size.height);
    const width = clamp(rect.width, 0, size.width - x);
    const height = clamp(rect.height, 0, size.height - y);
    return { x, y, width, height };
}
export function normalizeRect(rect, size) {
    return {
        x: rect.x / size.width,
        y: rect.y / size.height,
        width: rect.width / size.width,
        height: rect.height / size.height,
    };
}
/**
 * Reproduce the CSS `object-fit` geometry: how the source is scaled and offset
 * inside the DOM rectangle. For `cover` the source is scaled up until it fills
 * the rectangle, so the offsets are negative (source overflows the box).
 */
export function computeObjectFitTransform(domRect, sourceSize, objectFit, objectPosition = CENTER_OBJECT_POSITION) {
    const scaleX = domRect.width / sourceSize.width;
    const scaleY = domRect.height / sourceSize.height;
    const scale = objectFit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
    const renderedWidth = sourceSize.width * scale;
    const renderedHeight = sourceSize.height * scale;
    // object-position places the rendered content within the box. The default
    // {0.5, 0.5} reproduces centered behaviour: offset = (box - rendered) / 2.
    const posX = Number.isFinite(objectPosition.x) ? objectPosition.x : 0.5;
    const posY = Number.isFinite(objectPosition.y) ? objectPosition.y : 0.5;
    const offsetX = (domRect.width - renderedWidth) * posX;
    const offsetY = (domRect.height - renderedHeight) * posY;
    return { scale, renderedWidth, renderedHeight, offsetX, offsetY };
}
/**
 * Project the overlay rectangle into preview-source pixels, expand it by
 * `marginRatio` on every edge, and clamp to the source bounds.
 *
 * The result is identical for portrait and landscape orientations: there are no
 * viewport-specific correction factors. Geometry comes entirely from the DOM
 * rects, the source size and the object-fit mode.
 */
const ZERO_RECT = { x: 0, y: 0, width: 0, height: 0 };
export function buildPreviewCrop(args) {
    const { videoDomRect, overlayDomRect, videoSourceSize, objectFit, marginRatio } = args;
    const objectPosition = args.objectPosition ?? CENTER_OBJECT_POSITION;
    const warnings = [];
    // No implicit whole-frame fallback: unusable geometry returns an EXPLICIT
    // invalid result. The caller must reject the capture and ask for a retry — it
    // must never upload the full frame resized to the card aspect ratio.
    if (!isPositiveSize(videoSourceSize) ||
        !isUsableRect(videoDomRect) ||
        !isUsableRect(overlayDomRect)) {
        return {
            previewCropPx: ZERO_RECT,
            previewCropNormalized: ZERO_RECT,
            previewVisibleSourceRect: ZERO_RECT,
            valid: false,
            rejectionReason: 'invalid_preview_geometry',
            warnings: ['invalid_preview_geometry'],
        };
    }
    const transform = computeObjectFitTransform(videoDomRect, videoSourceSize, objectFit, objectPosition);
    // Overlay position relative to the video DOM rectangle origin.
    const overlayDomX = overlayDomRect.x - videoDomRect.x;
    const overlayDomY = overlayDomRect.y - videoDomRect.y;
    // Project overlay DOM coordinates into preview-source pixels.
    const cropX = (overlayDomX - transform.offsetX) / transform.scale;
    const cropY = (overlayDomY - transform.offsetY) / transform.scale;
    const cropWidth = overlayDomRect.width / transform.scale;
    const cropHeight = overlayDomRect.height / transform.scale;
    // Expand by the safety margin on every edge.
    //   expandedX = cropX - cropWidth * marginRatio
    //   expandedWidth = cropWidth * (1 + 2 * marginRatio)
    const expanded = {
        x: cropX - cropWidth * marginRatio,
        y: cropY - cropHeight * marginRatio,
        width: cropWidth * (1 + marginRatio * 2),
        height: cropHeight * (1 + marginRatio * 2),
    };
    const previewCropPx = clampRectToSource(expanded, videoSourceSize);
    const previewCropNormalized = normalizeRect(previewCropPx, videoSourceSize);
    // The portion of the source actually visible inside the DOM video rectangle.
    const previewVisibleSourceRect = clampRectToSource({
        x: -transform.offsetX / transform.scale,
        y: -transform.offsetY / transform.scale,
        width: videoDomRect.width / transform.scale,
        height: videoDomRect.height / transform.scale,
    }, videoSourceSize);
    // A clamped crop that collapsed to ~nothing is unusable (overlay almost
    // entirely outside the source). Reject rather than upload a sliver.
    const degenerate = previewCropPx.width < 1 || previewCropPx.height < 1;
    return {
        previewCropPx,
        previewCropNormalized,
        previewVisibleSourceRect,
        valid: !degenerate,
        rejectionReason: degenerate ? 'degenerate_overlay_crop' : null,
        warnings: degenerate ? [...warnings, 'degenerate_overlay_crop'] : warnings,
    };
}
/**
 * Compute the centered region of the native still that corresponds to the video
 * preview's field of view.
 *
 * Assumption: the video preview is a centered crop of the still's sensor area
 * with the preview's aspect ratio. For the common 16:9 preview inside a 4:3
 * still this yields a centered 16:9 band of the 4:3 image — i.e. the still
 * contains extra sensor area above/below that the preview never showed.
 *
 *  - native wider than preview aspect  → crop equal areas left and right;
 *  - native taller than preview aspect → crop equal areas top and bottom.
 *
 * NOTE: some devices change field of view, lens, or apply electronic
 * stabilization between video and still capture. This centered-region model
 * cannot detect that; Step 4's visual consistency check exists to catch it and
 * fall back to the video-frame crop.
 */
export function computeCenteredActiveRegion(nativeSize, previewAspectRatio) {
    if (!isPositiveSize(nativeSize) || !(previewAspectRatio > 0)) {
        return {
            x: 0,
            y: 0,
            width: Math.max(0, nativeSize?.width || 0),
            height: Math.max(0, nativeSize?.height || 0),
        };
    }
    const nativeAspect = nativeSize.width / nativeSize.height;
    if (nativeAspect > previewAspectRatio) {
        // Native is wider than the preview: crop the left and right edges.
        const height = nativeSize.height;
        const width = height * previewAspectRatio;
        return { x: (nativeSize.width - width) / 2, y: 0, width, height };
    }
    // Native is taller than (or equal to) the preview: crop top and bottom.
    const width = nativeSize.width;
    const height = width / previewAspectRatio;
    return { x: 0, y: (nativeSize.height - height) / 2, width, height };
}
/**
 * Map a preview-normalized crop into the native still through its active
 * region, then clamp to the native bounds.
 */
export function mapNormalizedCropToActiveRegion(normalized, activeRegion, nativeSize) {
    const mapped = {
        x: activeRegion.x + normalized.x * activeRegion.width,
        y: activeRegion.y + normalized.y * activeRegion.height,
        width: normalized.width * activeRegion.width,
        height: normalized.height * activeRegion.height,
    };
    return clampRectToSource(mapped, nativeSize);
}
/** Build the deterministic video-frame crop plan from a preview crop. */
export function planVideoCrop(preview, videoSourceSize, strategy = 'video_frame', extraWarnings = []) {
    return {
        previewCropPx: preview.previewCropPx,
        previewCropNormalized: preview.previewCropNormalized,
        sourceCropPx: preview.previewCropPx,
        sourceActiveRegionPx: {
            x: 0,
            y: 0,
            width: videoSourceSize.width,
            height: videoSourceSize.height,
        },
        strategy,
        warnings: [...preview.warnings, ...extraWarnings],
    };
}
/** Build the native-mapped crop plan from a preview crop and the native size. */
export function planNativeCrop(preview, nativeSize, previewAspectRatio) {
    const activeRegion = computeCenteredActiveRegion(nativeSize, previewAspectRatio);
    const sourceCropPx = mapNormalizedCropToActiveRegion(preview.previewCropNormalized, activeRegion, nativeSize);
    return {
        previewCropPx: preview.previewCropPx,
        previewCropNormalized: preview.previewCropNormalized,
        sourceCropPx,
        sourceActiveRegionPx: activeRegion,
        strategy: 'native_mapped',
        warnings: [...preview.warnings],
    };
}
/**
 * Compare two equal-length, mean-subtracted grayscale signatures and return a
 * similarity in `[0, 1]` (normalized cross-correlation remapped from `[-1, 1]`).
 * Pure: callers downscale crops to signatures before passing them in.
 */
export function compareGraySignatures(a, b) {
    if (!a || !b || a.length === 0 || a.length !== b.length) {
        return 0;
    }
    let dot = 0;
    let normA = 0;
    let normB = 0;
    for (let index = 0; index < a.length; index += 1) {
        dot += a[index] * b[index];
        normA += a[index] * a[index];
        normB += b[index] * b[index];
    }
    if (normA === 0 || normB === 0) {
        return 0;
    }
    const correlation = dot / Math.sqrt(normA * normB);
    return (correlation + 1) / 2;
}
/** Fraction of `a` (an overlay DOM rect) that lies inside `b` (the video DOM rect). */
export function rectContainmentRatio(a, b) {
    const area = a.width * a.height;
    if (!(area > 0))
        return 0;
    const ix = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
    const iy = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
    return (ix * iy) / area;
}
/** Minimum fraction of the overlay that must lie within the video viewport. */
export const MIN_OVERLAY_CONTAINMENT = 0.9;
/**
 * Authoritative validity check for the overlay→source mapping. Returns an
 * EXPLICIT result; the caller throws / retries on `!valid` and must never fall
 * back to the whole frame.
 */
export function validateOverlayMapping(args) {
    const { videoSourceSize, videoDomRect, overlayDomRect, previewCrop } = args;
    if (!isPositiveSize(videoSourceSize)) {
        return { valid: false, rejectionReason: 'video_metadata_not_ready' };
    }
    if (!isUsableRect(videoDomRect)) {
        return { valid: false, rejectionReason: 'video_rect_unusable' };
    }
    if (!isUsableRect(overlayDomRect)) {
        return { valid: false, rejectionReason: 'overlay_rect_unusable' };
    }
    if (rectContainmentRatio(overlayDomRect, videoDomRect) < MIN_OVERLAY_CONTAINMENT) {
        return { valid: false, rejectionReason: 'overlay_outside_video' };
    }
    if (!previewCrop || !previewCrop.valid || !isUsableRect(previewCrop.previewCropPx)) {
        return { valid: false, rejectionReason: previewCrop?.rejectionReason ?? 'invalid_preview_geometry' };
    }
    return { valid: true, rejectionReason: null };
}
/**
 * Decide the user-facing capture strategy. The video-frame overlay crop is the
 * authoritative production source; the native still is only ever selected when
 * the native flag is on AND a runtime alignment check verified it.
 */
export function selectCaptureSourceStrategy(args) {
    if (args.enableNativeMapping && args.nativeAttempted && args.nativeVerified) {
        return 'native_photo_verified';
    }
    if (args.enableNativeMapping && args.nativeAttempted) {
        return 'video_frame_fallback';
    }
    return 'video_overlay_exact';
}
/** Assemble PII-free capture diagnostics (no image data). Pure. */
export function buildCaptureDiagnostics(args) {
    return {
        side: args.side,
        selectedStrategy: args.selectedStrategy,
        videoSourceWidth: args.videoSourceSize.width,
        videoSourceHeight: args.videoSourceSize.height,
        videoDisplayRect: args.videoDomRect,
        overlayDisplayRect: args.overlayDomRect,
        sourceCropRect: args.sourceCropPx,
        sourceCropNormalized: args.sourceCropNormalized,
        objectFit: args.objectFit,
        objectPosition: args.objectPosition,
        mirroredPreview: args.mirroredPreview,
        outputWidth: args.outputWidth,
        outputHeight: args.outputHeight,
        outputAspectRatio: args.outputHeight > 0 ? args.outputWidth / args.outputHeight : 0,
        nativePhotoAttempted: args.nativePhotoAttempted,
        nativePhotoSelected: args.nativePhotoSelected,
        mappingValid: args.mappingValid,
        rejectionReason: args.rejectionReason,
    };
}
/** Parse a CSS `object-position` (e.g. "50% 50%", "left top") to fractions 0..1. */
export function parseObjectPosition(value) {
    if (!value)
        return CENTER_OBJECT_POSITION;
    const parts = value.trim().split(/\s+/);
    const axis = (token, horizontal) => {
        if (!token)
            return 0.5;
        const keyword = token.toLowerCase();
        if (keyword === 'center')
            return 0.5;
        if (horizontal && keyword === 'left')
            return 0;
        if (horizontal && keyword === 'right')
            return 1;
        if (!horizontal && keyword === 'top')
            return 0;
        if (!horizontal && keyword === 'bottom')
            return 1;
        if (keyword.endsWith('%')) {
            const pct = Number.parseFloat(keyword);
            return Number.isFinite(pct) ? clamp(pct / 100, 0, 1) : 0.5;
        }
        return 0.5; // px / unsupported -> default center (keeps current behaviour)
    };
    return { x: axis(parts[0], true), y: axis(parts[1], false) };
}
